import { useState, useEffect, useCallback, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { selectedSiteAtom } from '../state/sitesAtom';
import { campaignsApi } from '../api';

/**
 * Hook for fetching all ads (across campaigns)
 */
export function useAds(filters) {
  const selectedSite = useRecoilValue(selectedSiteAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Serialize filters for stable dependency
  const filtersKey = JSON.stringify(filters || {});
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // First get campaigns, then get ads for each campaign
      const params = {
        site: selectedSite !== 'all' ? selectedSite : undefined,
        ...(filtersRef.current || {}),
      };

      const campaignsResult = await campaignsApi.getAll(params);
      const campaigns = campaignsResult.data || [];

      // Extract date params for ads fetch
      const { start, end } = filtersRef.current || {};
      const adsParams = {};
      if (start) adsParams.start = start;
      if (end) adsParams.end = end;

      // Fetch ads for each campaign in parallel
      const adsPromises = campaigns.map(async (campaign) => {
        try {
          const adsResult = await campaignsApi.getAds(campaign.campaign_id || campaign.id, adsParams);
          const ads = adsResult.data || [];
          return ads.map((ad) => ({
            ...ad,
            campaign_id: campaign.campaign_id || campaign.id,
            campaign_name: campaign.campaign_name || campaign.name,
            advertiser_id: campaign.advertiser_id,
            advertiser_name: campaign.advertiser_name,
          }));
        } catch (err) {
          return [];
        }
      });

      const allAdsArrays = await Promise.all(adsPromises);
      const allAdsFlat = allAdsArrays.flat();

      // Deduplicate by ad_id — an ad in multiple campaigns appears once with merged campaigns array
      const adsMap = new Map();
      allAdsFlat.forEach(ad => {
        const id = ad.ad_id || ad.id;
        if (adsMap.has(id)) {
          const existing = adsMap.get(id);
          // Add this campaign to the existing ad's campaigns array if not already present
          if (!existing.campaigns.some(c => c.campaign_id === ad.campaign_id)) {
            existing.campaigns.push({ campaign_id: ad.campaign_id, campaign_name: ad.campaign_name });
          }
        } else {
          // Build campaigns array from ad.campaigns (server-provided) or from single campaign context
          const campaigns = ad.campaigns?.length
            ? ad.campaigns
            : [{ campaign_id: ad.campaign_id, campaign_name: ad.campaign_name }];
          adsMap.set(id, { ...ad, campaigns });
        }
      });

      setData(Array.from(adsMap.values()));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite, filtersKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for fetching ads for a specific campaign
 */
export function useCampaignAds(campaignId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!campaignId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await campaignsApi.getAds(campaignId);
      setData(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for ad mutations
 */
export function useAdMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createAd = useCallback(async (campaignId, formData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await campaignsApi.createAd(campaignId, formData);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAd = useCallback(async (campaignId, adId, data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await campaignsApi.updateAd(campaignId, adId, data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteAd = useCallback(async (campaignId, adId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await campaignsApi.deleteAd(campaignId, adId);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const duplicateAd = useCallback(async (campaignId, adId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await campaignsApi.duplicateAd(campaignId, adId);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createAd,
    updateAd,
    deleteAd,
    duplicateAd,
    loading,
    error,
  };
}

export default useAds;
